/**
 * huipil-pricing.js
 * Runs on product pages using template suffix "huipil".
 * Renders a region dropdown that drives Shopify's market selector via
 * /localization POST. No IP-based auto-switching — that fought the customer's
 * manual market choice and caused currency flips on the huipil page. Shopify
 * Markets' own geolocation recommendations handle first-visit suggestions.
 * Prices are set per-market by the HuipilPriceCron worker — no client-side
 * calculation needed.
 */

(function () {
  'use strict';

  const LOCATION_LABELS = {
    USA: 'USA (USD)',
    GBP: 'UK (GBP)',
    EUR: 'Europe (EUR)',
    AUD: 'Australia (AUD)',
    CAD: 'Canada (CAD)',
    ASIA: 'Asia',
    MXN: 'Latin America',
    ROW: 'Rest of World',
  };

  // Map pricing region → Shopify country code for /localization POST
  const REGION_TO_COUNTRY_CODE = {
    USA: 'US',
    GBP: 'GB',
    EUR: 'FR',
    AUD: 'AU',
    CAD: 'CA',
    ASIA: 'JP',
    MXN: 'MX',
    ROW: 'IN',
  };

  // Map ISO country code → pricing region
  // Comprehensive — covers all EU/EEA, French overseas, and common ASIA/MXN markets.
  // Countries not listed here default to ROW (Rest of World).
  const COUNTRY_TO_REGION = {
    // USA
    US: 'USA',
    // GBP
    GB: 'GBP',
    // AUD
    AU: 'AUD', NZ: 'AUD',
    // CAD
    CA: 'CAD',
    // MXN (Latin America)
    MX: 'MXN', CL: 'MXN', PE: 'MXN', CO: 'MXN', AR: 'MXN',
    EC: 'MXN', UY: 'MXN', CR: 'MXN', PA: 'MXN', GT: 'MXN',
    BO: 'MXN', PY: 'MXN', VE: 'MXN', HN: 'MXN', NI: 'MXN',
    SV: 'MXN', DO: 'MXN', CU: 'MXN', JM: 'MXN', BZ: 'MXN',
    HT: 'MXN', TT: 'MXN', BB: 'MXN', LC: 'MXN', VC: 'MXN',
    GD: 'MXN', AG: 'MXN', DM: 'MXN', KN: 'MXN', BS: 'MXN',
    // EUR — EU member states
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
    PT: 'EUR', BE: 'EUR', AT: 'EUR', SE: 'EUR', DK: 'EUR',
    FI: 'EUR', PL: 'EUR', HR: 'EUR', IE: 'EUR', CZ: 'EUR',
    RO: 'EUR', HU: 'EUR', BG: 'EUR', SK: 'EUR', SI: 'EUR',
    LT: 'EUR', LV: 'EUR', EE: 'EUR', CY: 'EUR', MT: 'EUR',
    LU: 'EUR', GR: 'EUR',
    // EUR — EEA + nearby
    NO: 'EUR', CH: 'EUR', IS: 'EUR', LI: 'EUR',
    // EUR — French overseas territories
    GP: 'EUR', MQ: 'EUR', GF: 'EUR', RE: 'EUR', YT: 'EUR',
    PM: 'EUR', BL: 'EUR', MF: 'EUR', TF: 'EUR', WF: 'EUR',
    PF: 'EUR', NC: 'EUR',
    // EUR — other European
    MC: 'EUR', AD: 'EUR', SM: 'EUR', VA: 'EUR', ME: 'EUR',
    RS: 'EUR', BA: 'EUR', AL: 'EUR', MK: 'EUR', XK: 'EUR',
    MD: 'EUR', UA: 'EUR', BY: 'EUR', TR: 'EUR',
    // ASIA (East, Southeast, South, Central Asia)
    JP: 'ASIA', SG: 'ASIA', HK: 'ASIA', KR: 'ASIA',
    MY: 'ASIA', ID: 'ASIA', PH: 'ASIA', VN: 'ASIA',
    TW: 'ASIA', CN: 'ASIA', BD: 'ASIA', LK: 'ASIA', PK: 'ASIA',
    NP: 'ASIA', MM: 'ASIA', KH: 'ASIA', LA: 'ASIA', MN: 'ASIA',
    KZ: 'ASIA', UZ: 'ASIA',
    // ROW (Rest of World — India, Middle East, Thailand, Brazil, Russia, South Africa)
    IN: 'ROW', TH: 'ROW', BR: 'ROW', RU: 'ROW', ZA: 'ROW',
    AE: 'ROW', SA: 'ROW', QA: 'ROW',
    KW: 'ROW', BH: 'ROW', OM: 'ROW', IL: 'ROW', JO: 'ROW',
  };

  function getCurrentRegion() {
    // __shopifyCountry is set in theme.liquid from localization.country.iso_code
    const countryCode = window.__shopifyCountry;
    if (countryCode && COUNTRY_TO_REGION[countryCode]) {
      return COUNTRY_TO_REGION[countryCode];
    }
    return 'ROW';
  }

  function switchMarket(region) {
    let countryCode = REGION_TO_COUNTRY_CODE[region];
    if (!countryCode) return;

    // For EUR, prefer the customer's last-used EU country over the default (FR)
    if (region === 'EUR') {
      const savedEU = sessionStorage.getItem('huipil_eu_country');
      if (savedEU) countryCode = savedEU;
    }

    // Build and submit a localization form (same as Shopify's country selector)
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.style.display = 'none';

    const method = document.createElement('input');
    method.name = '_method';
    method.value = 'PUT';
    form.appendChild(method);

    const country = document.createElement('input');
    country.name = 'country_code';
    country.value = countryCode;
    form.appendChild(country);

    const formType = document.createElement('input');
    formType.name = 'form_type';
    formType.value = 'localization';
    form.appendChild(formType);

    const returnTo = document.createElement('input');
    returnTo.name = 'return_to';
    returnTo.value = window.location.pathname;
    form.appendChild(returnTo);

    document.body.appendChild(form);
    form.submit();
  }

  function renderDropdown(container, currentRegion) {
    const select = container.querySelector('#huipil-location-select');
    if (!select) return;

    select.innerHTML = '';
    for (const [region, label] of Object.entries(LOCATION_LABELS)) {
      const option = document.createElement('option');
      option.value = region;
      option.textContent = label;
      if (region === currentRegion) option.selected = true;
      select.appendChild(option);
    }

    select.style.display = '';
    // select.style.marginTop = '8px';

    select.addEventListener('change', function () {
      if (this.value !== currentRegion) {
        // Replace dropdown with loading text before page reloads
        const row = select.closest('.huipil-location-row');
        if (row) {
          row.innerHTML = '<span class="ts-description-small">Fetching price…</span>';
        }
        switchMarket(this.value);
      }
    });
  }

  function init() {
    const container = document.querySelector('[data-huipil-pricing]');
    if (!container) return;

    const currentRegion = getCurrentRegion();

    // Remember the customer's specific EU country so switching back to EUR
    // restores their country (e.g. France) instead of defaulting
    if (currentRegion === 'EUR' && window.__shopifyCountry) {
      sessionStorage.setItem('huipil_eu_country', window.__shopifyCountry);
    }

    renderDropdown(container, currentRegion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
